<template>
  <v-app
    class="app"
    :class="[{ 'app--loading': loading, 'hidden': mounting }]"
    @transitionend="onTransitionEnd($event)"
    @transitionrun="onTransitionStart($event)"
  >
    <NavBar v-if="!hideHeader" />

    <v-main>
      <BannerNotificationList />

      <NuxtLayout>
        <NuxtPage />
      </NuxtLayout>
    </v-main>

    <Footer :variant="footerVariant" />

    <ToastAlertList />

    <ConfirmationDialog />

    <FormDialog />

    <ImageFocusDialog />

    <MapDialog />
  </v-app>
</template>

<script setup lang="ts">
import ElementCountMap from '~/utils/elementCountMap';

const router = useRouter();
const { data, signOut } = useAuth();
const toastAlerts = useToastAlerts();
const currentMeta = computed(() => router.currentRoute.value.meta);
const hideHeader = computed(() => !!currentMeta.value.hideHeader);
const footerVariant = computed(() => currentMeta.value.footerVariant);

// `signOut` only works on the client side
if (process.client) {
  effect(async () => {
    if (data.value?.error) {
      // If there's an "error" in the session, the server token is no longer valid
      const currentRoute = router.currentRoute.value;
      const isLoginPage = currentRoute.meta.isLoginPage;

      // Sign out without refreshing page altogether
      await signOut({ redirect: false });
      // If we're not on the login page, let's navigate there without page reload
      if (!isLoginPage) {
        router.push(`/login?next=${currentRoute.fullPath}`);
      }
      toastAlerts.add({
        type: 'warning',
        message: 'Your session has expired, please log in again.',
        dismissable: true,
        dismissAfter: 0,
      });
    }
  });
}

/**
 * Set the transition duration to 0s for certains elements within
 * the `app--loading` class until later.
 */
const loading = ref(true);
// Hides the app until a short time out.
const mounting = ref(true);
const transitioningElements = new ElementCountMap();

onMounted(() => {
  /**
   * Set mounting to `false` with a small enough delay that it's
   * imperceptable, but still waits enough event cycles that the
   * navigation finishes transitioning.
   */
  setTimeout(() => mounting.value = false, 15);

  /**
   * Sets loading to `false` (if it isn't already) after 300ms
   * as a fallback in case the `transitionend` handler doesn't
   * pick up any transitions ending.
   */
  setTimeout(() => loading.value = false, 300);
});

/**
 * Returns true if the element is a `<v-app-bar>` or
 * `<v-navigation-drawer>` element, based on CSS classes.
 *
 * @param element The element to check CSS classes on
 */
const isElementAppBarOrNavDrawer = (element: HTMLElement) => {
  const classes = ['v-app-bar', 'v-navigation-drawer'];
  return classes.some(c => element.classList.contains(c));
};

/**
 * Called anytime a component within the app starts a transition,
 * specifically filters down to the app bar and navigation drawer.
 * Adds each transitioning element to an array to track when
 * they've all completed their initial transitions.
 *
 * @param element The element which is starting a transition
 */
const onTransitionStart = ({ target: element }: { target: HTMLElement }) => {
  if (!loading.value) {
    return;
  }
  if (!isElementAppBarOrNavDrawer(element)) {
    return;
  }

  transitioningElements.increment(element);
};

/**
 * Called anytime a component within the app ends a transition,
 * specifically filters down to the app bar and navigation drawer.
 * Once all component transitions are complete, marks `loading`
 * as false to return specified transition durartions to normal.
 *
 * @param element The element which is ending a transition
 */
const onTransitionEnd = ({ target: element }: { target: HTMLElement }) => {
  if (!loading.value) {
    return;
  }
  if (!isElementAppBarOrNavDrawer(element)) {
    return;
  }

  const remainingCount = transitioningElements.decrement(element);
  // Once all components are done transiting, loading is done.
  if (remainingCount === 0 && transitioningElements.isEmpty()) {
    loading.value = false;
  }
};
</script>
